// One message → one DOM node. Text goes in through textContent: the API
// does not serve sanitized HTML today, and a customer's page is not the
// place to find out a sanitizer regressed.

import type { Attachment, Citation, Message } from './types'

/**
 * `brand` is what the assistant's replies are signed with — the business's
 * own name. A visitor on their site has no idea who airys is.
 */
export function renderMessage(m: Message, helpCenterUrl?: string, brand?: string): HTMLElement {
  // System events (takeover, release…) are metadata, shown as a divider.
  if (m.role === 'system') {
    const div = document.createElement('div')
    div.className = 'system-event'
    div.textContent = m.content
    return div
  }

  const row = document.createElement('div')
  row.className = `row ${m.role === 'user' ? 'user' : 'assistant'}`
  row.dataset.messageId = m.id

  // Same shapes as the admin inbox: who said it sits inside the bubble.
  const bubble = document.createElement('div')
  bubble.className = `msg msg-${m.role}`
  const label = document.createElement('div')
  label.className = 'msg-label'
  label.textContent =
    m.role === 'user' ? 'You' : m.role === 'agent' ? 'Support' : (brand ?? 'Support')
  const body = document.createElement('div')
  body.className = 'msg-body'
  if (m.content) renderText(body, m.content)
  bubble.append(label, body)
  if (!m.content) body.remove()
  for (const a of m.attachments ?? []) bubble.appendChild(renderAttachment(a))
  row.appendChild(bubble)

  if (m.citations?.length) row.appendChild(renderCitations(m.citations, helpCenterUrl))
  row.appendChild(renderTime(m.createdAt))
  return row
}

function renderAttachment(a: Attachment): HTMLElement {
  if (a.mimeType.startsWith('image/') && a.url) {
    const link = document.createElement('a')
    link.className = 'msg-image'
    link.href = a.url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    const img = document.createElement('img')
    img.src = a.url
    img.alt = a.originalFilename ?? 'Image'
    img.loading = 'lazy'
    link.appendChild(img)
    return link
  }
  const file = document.createElement(a.url ? 'a' : 'span')
  file.className = 'msg-file'
  file.textContent = a.originalFilename ?? a.mimeType
  if (file instanceof HTMLAnchorElement && a.url) {
    file.href = a.url
    file.target = '_blank'
    file.rel = 'noopener noreferrer'
  }
  return file
}

// Relative, like the inbox; widget.ts refreshes these once a minute.
export function renderTime(iso: string): HTMLElement {
  const time = document.createElement('time')
  time.className = 'msg-time'
  time.dateTime = iso
  time.title = new Date(iso).toLocaleString()
  time.textContent = timeAgo(iso)
  return time
}

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

export function timeAgo(iso: string, now = Date.now()): string {
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000)
  const abs = Math.abs(seconds)
  if (abs < 45) return 'just now'
  if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute')
  if (abs < 86_400) return relative.format(Math.round(seconds / 3600), 'hour')
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

// The visitor's own message while the server has not echoed it back yet.
export function renderPending(localId: string, content: string, previewUrl?: string): HTMLElement {
  const row = document.createElement('div')
  row.className = 'row user'
  row.dataset.localId = localId
  const bubble = document.createElement('div')
  bubble.className = 'msg msg-user msg-pending'
  const label = document.createElement('div')
  label.className = 'msg-label'
  label.textContent = 'You'
  bubble.appendChild(label)
  if (content) {
    const body = document.createElement('div')
    body.className = 'msg-body'
    renderText(body, content)
    bubble.appendChild(body)
  }
  if (previewUrl) {
    const img = document.createElement('img')
    img.className = 'msg-image'
    img.src = previewUrl
    img.alt = ''
    bubble.appendChild(img)
  }
  row.appendChild(bubble)
  const time = document.createElement('div')
  time.className = 'msg-time'
  time.textContent = 'Sending…'
  row.appendChild(time)
  return row
}

const URL_RE = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/g

// Plain text with line breaks kept and bare URLs made clickable.
export function renderText(container: HTMLElement, text: string) {
  const lines = text.split('\n')
  lines.forEach((line, i) => {
    if (i > 0) container.appendChild(document.createElement('br'))
    let last = 0
    for (const match of line.matchAll(URL_RE)) {
      const start = match.index ?? 0
      if (start > last) container.appendChild(document.createTextNode(line.slice(last, start)))
      const a = document.createElement('a')
      a.href = match[0]
      a.target = '_blank'
      a.rel = 'noopener noreferrer'
      a.textContent = match[0]
      container.appendChild(a)
      last = start + match[0].length
    }
    if (last < line.length) container.appendChild(document.createTextNode(line.slice(last)))
  })
}

function renderCitations(citations: Citation[], helpCenterUrl?: string): HTMLElement {
  const row = document.createElement('div')
  row.className = 'citations'
  const base = helpCenterUrl?.replace(/\/+$/, '')
  for (const c of citations) {
    // Without a help center address the title is still useful, as text.
    const chip = document.createElement(base ? 'a' : 'span')
    chip.className = 'citation'
    chip.textContent = c.title
    if (chip instanceof HTMLAnchorElement && base) {
      chip.href = `${base}/articles/${encodeURIComponent(c.slug)}`
      chip.target = '_blank'
      chip.rel = 'noopener noreferrer'
    }
    row.appendChild(chip)
  }
  return row
}
